import { useFlags } from '@equipmentshare/feature-flags'
import { isProduction } from './environment'

export type MuiRolloutView =
	| 'add-to-inventory'
	| 'inventory'
	| 'inventory-adjustments'
	| 'inventory-locations'
	| 'item'
	| 'item-masters'
	| 'parts'
	| 'permissions'
	| 'product-classes'
	| 'providers'
	| 'reports'
	| 'requests'

// Flip a view to `true` in its release MR. Until then it stays EUI even if a flag lists it,
// so the PO can add a key ahead of time without exposing a half-built view.
// Who sees a released view is the PO's call, in LaunchDarkly — see docs/ds2-to-mui-migration.md.
export const MUI_VIEWS_RELEASED: Record<MuiRolloutView, boolean> = {
	'add-to-inventory': false,
	'inventory': false,
	'inventory-adjustments': false,
	'inventory-locations': false,
	'item': false,
	'item-masters': false,
	'parts': true,
	'permissions': false,
	'product-classes': false,
	'providers': false,
	'reports': false,
	'requests': false,
}

// Each flag serves a JSON list of view keys: `internal` to all of company 1854, `beta` to the PO's cohort.
export type MuiRolloutFlags = {
	costentoryEnableInventoryMuiInternal?: unknown,
	costentoryEnableInventoryMuiBeta?: unknown,
}

function listsView( value: unknown, view: MuiRolloutView ) {
	return Array.isArray( value ) && value.includes( view )
}

export function isMuiEnabled( view: MuiRolloutView, released: boolean, flags: MuiRolloutFlags ): boolean {
	// An unevaluated or malformed flag lists nothing, so the shipped EUI view renders.
	return released && (
		listsView( flags.costentoryEnableInventoryMuiBeta, view ) ||
		listsView( flags.costentoryEnableInventoryMuiInternal, view )
	)
}

const warnedKeys = new Set<string>()

// The lists are hand-edited in LaunchDarkly, where a misspelled key would otherwise fail silently.
export function warnUnknownViews( flags: MuiRolloutFlags ) {
	for ( const value of [ flags.costentoryEnableInventoryMuiBeta, flags.costentoryEnableInventoryMuiInternal ] ) {
		if ( !Array.isArray( value ) ) {
			continue
		}
		for ( const key of value ) {
			if ( !( key in MUI_VIEWS_RELEASED ) && !warnedKeys.has( key ) ) {
				warnedKeys.add( key )
				// eslint-disable-next-line no-console
				console.warn( `MUI rollout flag lists an unknown view "${key}". Known views: ${Object.keys( MUI_VIEWS_RELEASED ).join( ', ' )}` )
			}
		}
	}
}

export function useMuiRollout( view: MuiRolloutView ): boolean {
	const flags = useFlags() as MuiRolloutFlags
	if ( !isProduction() ) {
		warnUnknownViews( flags )
	}
	return isMuiEnabled( view, MUI_VIEWS_RELEASED[ view ], flags )
}
